import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { StyledEngineProvider, ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { QueryClient, QueryClientProvider } from 'react-query';
import { useMsal } from '@azure/msal-react';

import Routes from './routes';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { API_FAIL_TEXT, NAVIGATE_PATH, USER_TYPE } from './constants';
import AlertBox from './components/common/alert/AlertBox';
import WarningIcon from './assets/images/icons/warning_amber.svg';

// project imports
import NavigationScroll from './components/extended/NavigationScroll';
import { setAccountDetail } from './redux/auth/AuthSlice';
import Toast from './components/common/snackbar/Snackbar';
import { SnackbarProvider, closeSnackbar } from 'notistack';
import { THEME_CONFIG } from './constants/config';

// defaultTheme
import themes from './themes';
import AuthFacade from './redux/auth/Auth.facade';
import useAuthExternalUser from './hooks/useAuthExternalUser';
import CommonFacade from './redux/common/Common.facade';
import useAuth from './hooks/useAuth';
import useCustomNavigate from './hooks/useNavigator';
const queryClient = new QueryClient();
import { parseJwt } from './utils/common';
function App() {
  const REFRESH_THRESHOLD = 1200; // Before 20 minutes (1200 seconds) token expire > New token will generate
  const TOKEN_CHECK_INTERVAL = 300000; // 5 minute in milliseconds
  let ranOnce = false;
  let ranOnceUser = false;
  const { goToPage } = useCustomNavigate();
  const dispatch = useDispatch();
  const { accounts } = useMsal();
  const { externalUserLogout, externalUserDetails, getUserType } =
    useAuthExternalUser();
  const { getLoginAccessToken, refreshToken, logout } = useAuth();
  const { setAuthorizationData, setHideSidebar } = AuthFacade();
  const { getUserPermissions, setUserLoginActivity } = CommonFacade();
  const {
    accTokenUserRole,
    authorizationData,
    refreshTokenCount,
    accountDetail,
  } = useSelector((state) => state.auth);
  const { userPermissions, userActivityFulfilled } = useSelector(
    (state) => state.common
  );
  const userType = getUserType();
  const urlParams = new URLSearchParams(window.location.search);
  const clientId = urlParams?.get('client_id');
  const code = urlParams?.get('code');
  const error = urlParams?.get('error');
  if (clientId && code) {
    sessionStorage.setItem('userType', USER_TYPE.externalUser);
  } else if (userType === undefined || userType === null) {
    sessionStorage.setItem('userType', USER_TYPE.internalUser);
  }
  const setStateForExtAuthorization = () => {
    let externalUserData = externalUserDetails();
    setAuthorizationData(externalUserData);
    setHideSidebar(true);
  };
  const alertAction = [
    {
      name: 'Re-Login',
      class: '',
      action: () => logout(), // Close the dialog
    },
  ];
  useEffect(() => {
    if (ranOnce === false) {
      // Check if external user, skip MSAL authentication
      if (code && clientId) {
        sessionStorage.setItem('userType', USER_TYPE.externalUser);
        if (sessionStorage.getItem('userActivity') !== 'true') {
          sessionStorage.setItem('userActivity', false);
        }
        setStateForExtAuthorization();
      } else if (userType !== USER_TYPE.externalUser) {
        sessionStorage.setItem('userType', USER_TYPE.internalUser);
        if (sessionStorage.getItem('userActivity') !== 'true') {
          sessionStorage.setItem('userActivity', false);
        }
        getLoginAccessToken();
      } else if (userType === USER_TYPE.externalUser && error) {
        externalUserLogout();
      } else if (userType === USER_TYPE.externalUser) {
        setStateForExtAuthorization();
      }
      ranOnce = true;
    }
  }, []);

  useEffect(() => {
    if (accountDetail) {
      const checkTokenExpiry = async () => {
        const currentAccessToken = sessionStorage.getItem('token');
        if (currentAccessToken) {
          const decodeToken = JSON.parse(parseJwt(currentAccessToken));
          const currentTime = Math.floor(Date.now() / 1000); // Current time
          const timeUntilExpiry = decodeToken.exp - currentTime;
          console.log(timeUntilExpiry);
          // session is going going to expire then befor 5 mints token will get refresh here.
          if (timeUntilExpiry <= REFRESH_THRESHOLD) {
            if (userType === USER_TYPE.internalUser) {
              await refreshToken(accountDetail);
            }
          }
        }
      };
      const checkTokenIntervalTimer = setInterval(async () => {
        await checkTokenExpiry();
      }, TOKEN_CHECK_INTERVAL);
      checkTokenExpiry(); // Check token expiry immediately after mounting
      return () => {
        clearInterval(checkTokenIntervalTimer);
      };
    }
  }, [accountDetail]);

  // GET PERMISSIONS FOR INTERNAL USER
  const [logoutAlert, setLogoutAlert] = useState(false);
  useEffect(() => {
    if (refreshTokenCount > 0) {
      if (refreshTokenCount > 10) {
        setLogoutAlert(true);
      } else if (userType === USER_TYPE.internalUser) {
        refreshToken(accountDetail);
      }
    } else {
      if (ranOnceUser === false && accTokenUserRole != '') {
        sessionStorage.setItem('token', accTokenUserRole);
        if (userType === USER_TYPE.internalUser) {
          let payload = { token: accTokenUserRole, userType: userType };
          getUserPermissions(payload);
          sessionStorage.setItem('token', accTokenUserRole);
        }
        ranOnceUser = true;
      }
    }
  }, [accTokenUserRole, refreshTokenCount]);
  // GET PERMISSIONS FOR EXTERNAL USER
  const externalUserPermission = (externalUserData) => {
    let userData = structuredClone(externalUserData?.userProfile?.myNCRUserProfileResp);
    let clientIds = [];
    userData?.memberOf?.forEach((mcnId) => {
      if(!mcnId?.accountNumber?.startsWith('EE_') && !mcnId?.accountNumber.startsWith('CE_')){
        //As discussed with NCR(IJ) team, we added this condition for 'EE_' and 'CE_' to exclude from list. These are not actual clientIds/MCNs.
        clientIds.push(mcnId?.accountNumber);
      }
    });
    let payload = {
      login_name: userData?.login,
      first_Name: userData?.firstName,
      last_Name: userData?.lastname,
      middle_Name: null,
      client_ids: clientIds,
      title: userData?.jobTitle,
      nickname: null,
      date_last_login: new Date(),
      date_status_code_last_changed: new Date(),
      suffix: null,
      userType: userType,
      forwarding_email_address: userData?.email,
    };
    getUserPermissions(payload);
  };
  let runOnceData = false;
  useEffect(() => {
    if (userType === USER_TYPE.externalUser && !runOnceData) {
      if (userPermissions === null && authorizationData?.length !== 0) {
        externalUserPermission(authorizationData);
        sessionStorage.setItem('token', authorizationData.accessToken.id_token);
        if (clientId && code) {
          goToPage(NAVIGATE_PATH.eTrack);
        }
        runOnceData = true;
      }
    }
  }, [authorizationData]);

  useEffect(() => {
    if (
      sessionStorage.getItem('userActivity') === 'false' &&
      userPermissions &&
      userType === USER_TYPE.internalUser
    ) {
      let payload = {
        userId: userPermissions?.userLoginInfo.userId
          ? userPermissions?.userLoginInfo.userId
          : null,
        logonName: userPermissions?.userLoginInfo?.quicklookId || null,
        success: 'y',
        adminLogon: null,
        clientID: '0',
      };
      setUserLoginActivity(payload);
    }
  }, [userPermissions]);

  useEffect(() => {
    if (userActivityFulfilled === true && userType === USER_TYPE.internalUser) {
      sessionStorage.setItem('userActivity', true);
    }
  }, [userActivityFulfilled]);

  useEffect(() => {
    if (accounts?.length > 0) {
      dispatch(setAccountDetail(accounts[0]));
      getLoginAccessToken();
    }
  }, [accounts]);


  return (
  <>
    <QueryClientProvider client={queryClient}>
      <StyledEngineProvider>
        <ThemeProvider theme={themes(THEME_CONFIG)}>
          <CssBaseline />
          <NavigationScroll>
            <SnackbarProvider
              action={(snackbarId) => (
                <IconButton  onClick={() => closeSnackbar(snackbarId)} className={'closeSnackBar'}>
                  <CloseIcon />
                </IconButton>
              )}
              maxSnack={4}
              iconVariant={{
                success: <TaskAltIcon />,
                error: <ErrorOutlineIcon />,
                warning: <WarningAmberIcon />,
              }}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
              <Toast />
              <Routes />
            </SnackbarProvider>
          </NavigationScroll>
        </ThemeProvider>
      </StyledEngineProvider>
    </QueryClientProvider>
    <AlertBox
      open={logoutAlert}
      onClose={() => logout()}
      title={API_FAIL_TEXT.loginAgain}
      actions={alertAction}
      avatars={<img src={WarningIcon} alt="warning" />}
    />
  </>
  );
}

export default App;
